import { PropsWithChildren, useCallback, useEffect } from 'react';
import { BannerType, useModal, useToast } from '@pelando/components';
import { useTranslation } from '@/presentation/hooks/useTranslation';
import ProfilePageContainer from './component';
import { ProfileUser } from '../../../../domain/entities/User';
import {
  getUsersFollowedByCurrentUser,
  followUser,
  unfollowUser,
} from '../../../../infra/api/followers/endpoints';
import { useShowUnfollowModal } from '../../../components/UnfollowModal';
import { HEADER_HEIGHT } from '../../../components/Header/styles';
import {
  getFileUploadErrorText,
  getMaxFileSizeUploadErrorText,
  isMaxFileSizeUploadError,
} from '../../../services/errors';
import { useApiCache } from '../../../hooks/useApiCache';
import { updateNickname } from '../../../../infra/api/user/helpers/updateNickname';
import { updateAvatar } from '../../../../infra/api/user/helpers/updateAvatar';
import { useCurrentUserCache } from '../../../hooks/useCurrentUserCache';

type EnhancedProfilePageContainerProps = PropsWithChildren<{
  profileUser: Omit<ProfileUser, 'comments' | 'offers'>;
}>;

function EnhancedProfilePageContainer({
  profileUser,
  children,
}: EnhancedProfilePageContainerProps) {
  const { t } = useTranslation('common');
  const { showUnfollowModal } = useShowUnfollowModal();
  const { showToast } = useToast();
  const { closeModal } = useModal();

  const { loggedUser } = useCurrentUserCache();

  const followersCache = useApiCache(getUsersFollowedByCurrentUser.cache$);

  const getUserIsFollowed = (id: string) => !!followersCache?.[id];

  useEffect(() => {
    if (!profileUser?.id) return;
    getUsersFollowedByCurrentUser.requestAsPromise({
      userIds: [profileUser?.id],
    });
  }, [profileUser?.id]);

  const handleUnfollowUser = (userId: string) => {
    unfollowUser
      .requestAsPromise({ userId })
      .then(() => closeModal())
      .catch(() => closeModal());
  };

  const handleUnfollow = ({ userId }: { userId: string }) => {
    if (!profileUser) return;

    showUnfollowModal({
      onConfirm: () => handleUnfollowUser(userId),
      nickname: profileUser.nickname,
      avatar: profileUser.image?.url,
    });
  };

  const handleUpdateAvatar = useCallback(
    ({ file }: { file: File }) => {
      updateAvatar(file).catch(({ error }) => {
        if (!error) return;

        if (isMaxFileSizeUploadError(error)) {
          showToast({
            topPosition: HEADER_HEIGHT,
            text: t(getMaxFileSizeUploadErrorText()),
            bannerType: BannerType.ERROR,
            duration: 6000,
          });

          return;
        }

        showToast({
          topPosition: HEADER_HEIGHT,
          text: t(getFileUploadErrorText()),
          bannerType: BannerType.ERROR,
        });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showToast]
  );

  return (
    <ProfilePageContainer
      userData={profileUser}
      isFollowing={getUserIsFollowed(profileUser?.id)}
      onFollow={followUser.requestAsPromise}
      onUnfollow={handleUnfollow}
      updateNickname={updateNickname}
      onUpdateAvatar={handleUpdateAvatar}
      isOwner={!!loggedUser && loggedUser?.id === profileUser?.id}
      isLogged={!!loggedUser}
    >
      {children}
    </ProfilePageContainer>
  );
}

export default EnhancedProfilePageContainer;
